import { ChangeEvent, MouseEvent, ReactNode, useMemo, useRef, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $patchStyleText } from '@lexical/selection';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CodeIcon from '@mui/icons-material/Code';
import CreateIcon from '@mui/icons-material/Create';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import SubscriptIcon from '@mui/icons-material/Subscript';
import SuperscriptIcon from '@mui/icons-material/Superscript';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';
import { $getSelection, FORMAT_TEXT_COMMAND, TextFormatType } from 'lexical';
import { useDebounceCallback } from 'usehooks-ts';

import { useToolbarContext } from '../../ToolbarContext';
import FontIcon from '../FontIcon';
import IconMenuItem from '../IconMenuItem';
import ToggleButtonGroup from '../ToggleButtonGroup';
import ToolbarButton from '../ToolbarButton';

const TEXT_FORMAT_TYPES = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'subscript',
  'superscript',
  'lowercase',
  'uppercase',
  'capitalize',
  'highlight',
  'code',
] as const;

type FormatAction = TextFormatType | 'toggleMenu';

type ButtonMap = Record<
  TextFormatType,
  {
    icon: ReactNode;
    label: string;
    shortcut?: string;
  }
>;

interface TextFormatOptionsProps {
  menuOnlyActions?: TextFormatType[];
}

const buttonMap: ButtonMap = {
  bold: {
    label: 'Bold',
    icon: <FormatBoldIcon />,
  },
  italic: {
    label: 'Italic',
    icon: <FormatItalicIcon />,
  },
  underline: {
    label: 'Underline',
    icon: <FormatUnderlinedIcon />,
  },
  strikethrough: {
    label: 'Strikethrough',
    icon: <FormatStrikethroughIcon />,
  },
  subscript: {
    label: 'Subscript',
    icon: <SubscriptIcon />,
  },
  superscript: {
    label: 'Superscript',
    icon: <SuperscriptIcon />,
  },
  lowercase: {
    label: 'Lowercase',
    icon: <FontIcon>abc</FontIcon>,
  },
  uppercase: {
    label: 'Uppercase',
    icon: <FontIcon>ABC</FontIcon>,
  },
  capitalize: {
    label: 'Capitalize',
    icon: <FontIcon>Tt</FontIcon>,
  },
  highlight: {
    label: 'Highlight',
    icon: <CreateIcon />,
  },
  code: {
    label: 'Code',
    icon: <CodeIcon />,
  },
};

export function TextFormatOptions({ menuOnlyActions = [] }: TextFormatOptionsProps) {
  const [editor] = useLexicalComposerContext();
  const { activeTextFormats, fontColor, bgColor } = useToolbarContext();
  const fontColorInputRef = useRef<HTMLInputElement>(null);
  const bgColorInputRef = useRef<HTMLInputElement>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const buttonsToDisplay = useMemo(
    () => TEXT_FORMAT_TYPES.filter((value) => !menuOnlyActions.includes(value)),
    [menuOnlyActions],
  );

  const updateFontColor = useDebounceCallback((value: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if (selection !== null) {
        $patchStyleText(selection, {
          color: value,
        });
      }
    });
  }, 100);

  const updateBgColor = useDebounceCallback((value: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if (selection !== null) {
        $patchStyleText(selection, {
          'background-color': value,
        });
      }
    });
  }, 100);

  const handleFormatAction = (formatAction: TextFormatType) => {
    if (!formatAction) return;

    // if a text format command was clicked
    if ((TEXT_FORMAT_TYPES as readonly TextFormatType[]).includes(formatAction)) {
      // dispatch format text command to toggle the format
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, formatAction);
      return;
    }

    throw new TypeError(`Unhandled format action: ${formatAction}`);
  };

  const handleChange = (event: MouseEvent<HTMLElement> /*, newFormats: TextFormatType[]*/) => {
    // get the format action that was clicked
    const formatAction = event.currentTarget.getAttribute('value') as FormatAction | undefined;

    if (!formatAction) return;

    // if the more options menu was clicked
    if (formatAction === 'toggleMenu') {
      setMenuAnchorEl(event.currentTarget);
      return;
    }

    handleFormatAction(formatAction);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleFontColorChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateFontColor(event.target.value);
  };

  const handleBgColorChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateBgColor(event.target.value);
  };

  return (
    <>
      <ToggleButtonGroup value={Array.from(activeTextFormats)} onChange={handleChange} aria-label="text formatting">
        {/* toolbar buttons */}
        {buttonsToDisplay.map((formatType) => {
          const { label: title, icon: children } = buttonMap[formatType];
          return (
            <ToolbarButton key={formatType} value={formatType} tooltip={title}>
              {children}
            </ToolbarButton>
          );
        })}

        {/* toolbar button to open menu for menu only actions */}
        {menuOnlyActions.length > 0 && (
          <ToolbarButton value="toggleMenu">
            <Typography fontSize={18} lineHeight={1}>
              Aa
            </Typography>
            <ArrowDropDownIcon />
          </ToolbarButton>
        )}
      </ToggleButtonGroup>

      {/* font color and background color buttons */}
      <ToggleButtonGroup>
        <ToolbarButton value="fontColor" tooltip="Font Color" onClick={() => fontColorInputRef.current?.click()}>
          <FormatColorTextIcon sx={{ color: fontColor, stroke: 'black', strokeWidth: 0.2 }} />
          <ArrowDropDownIcon />
          <input
            type="color"
            ref={fontColorInputRef}
            value={fontColor}
            onChange={handleFontColorChange}
            style={{
              position: 'absolute',
              bottom: 0,
              visibility: 'hidden',
            }}
          />
        </ToolbarButton>

        <ToolbarButton value="bgColor" tooltip="Background Color" onClick={() => bgColorInputRef.current?.click()}>
          <FormatColorFillIcon sx={{ color: bgColor, stroke: 'black', strokeWidth: 0.2 }} />
          <ArrowDropDownIcon />
          <input
            type="color"
            ref={bgColorInputRef}
            value={bgColor}
            onChange={handleBgColorChange}
            style={{
              position: 'absolute',
              bottom: 0,
              visibility: 'hidden',
            }}
          />
        </ToolbarButton>
      </ToggleButtonGroup>

      {/* menu for menu only actions */}
      <Menu anchorEl={menuAnchorEl} open={!!menuAnchorEl} onClose={handleMenuClose}>
        {menuOnlyActions.map((action) => {
          const { label, icon, shortcut } = buttonMap[action];
          return (
            <IconMenuItem
              key={action}
              label={label}
              icon={icon}
              shortcut={shortcut}
              onClick={() => handleFormatAction(action)}
              selected={activeTextFormats.has(action)}
            />
          );
        })}
      </Menu>
    </>
  );
}

export default TextFormatOptions;
